import React, { useCallback, useContext, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RoleDto } from 'api/Identity/Types/Role'
import { Loading } from 'components/Loading'
import { ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'

import {
  EditRoleFormValues,
  EditRoleGeneralForm,
  EditRoleGeneralFormHandle,
} from './EditRoleGeneralForm'
import {
  AddRolePermissionFormValues,
  EditRolePermissionForm,
  EditRolePermissionFormHandle,
} from './EditRolePermissionForm'

export enum EditRoleTab {
  GENERAL = 'General Settings',
  PERMISSIONS = 'Property Permission Overrides',
}

interface Props {
  isOpen?: boolean
  onClose(): void
  role: RoleDto
  tab: EditRoleTab
  permissions: PermissionDto[]
  onSubmitRole(values: EditRoleFormValues): Promise<void>
  onSubmitPermissions(values: AddRolePermissionFormValues): Promise<void>
}

export const EditRolePage: React.FC<Props> = ({
  isOpen,
  onClose,
  role,
  permissions,
  tab,
  onSubmitRole,
  onSubmitPermissions,
}) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [activeTab, setActiveTab] = React.useState<EditRoleTab>(tab)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const setToast = useContext(ToastContext)
  const generalFormRef = React.useRef<EditRoleGeneralFormHandle>(null)
  const permissionFormRef = React.useRef<EditRolePermissionFormHandle>(null)

  const clientLabelsCallback = useCallback(clientsApiClient.getClientsSecuredPropertiesLabels, [])
  const tradingAccountLabelsCallback = useCallback(
    tradingAccountsApiClient.getTradingAccountsSecuredPropertiesLabels,
    []
  )
  const partnershipsAccountLabelsCallback = useCallback(
    partnershipsApiClient.getPartnershipAccountsSecuredPropertiesLabels,
    []
  )

  const { data: clientLabels, isLoading: isLoadingClientLabels } = useFetchOne(clientLabelsCallback)
  const { data: tradingAccountLabels, isLoading: isLoadingTradingAccountLabels } = useFetchOne(
    tradingAccountLabelsCallback
  )
  const { data: partnershipsAccountLabels, isLoading: isLoadingPartnershipsLabels } = useFetchOne(
    partnershipsAccountLabelsCallback
  )

  useEffect(() => {
    setActiveTab(tab)
  }, [tab])

  const isLoading =
    isLoadingTradingAccountLabels || isLoadingClientLabels || isLoadingPartnershipsLabels

  const handleSave = async () => {
    const activeForm =
      activeTab === EditRoleTab.GENERAL ? generalFormRef.current : permissionFormRef.current

    if (!activeForm || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      await activeForm.submit()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex h-[90vh] max-w-250 flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>{`Edit Role: ${role.name}`}</DialogTitle>
        </DialogHeader>

        <div className='min-h-0 min-w-0 flex-1 overflow-hidden'>
          <Loading isLoading={isLoading}>
            <Tabs
              value={activeTab}
              className='flex h-full min-h-0 min-w-0 flex-col gap-4'
              onValueChange={(v) => setActiveTab(v as EditRoleTab)}
            >
              <TabsList className='shrink-0 w-fit'>
                <TabsTrigger value={EditRoleTab.GENERAL}>{EditRoleTab.GENERAL}</TabsTrigger>
                <TabsTrigger value={EditRoleTab.PERMISSIONS}>{EditRoleTab.PERMISSIONS}</TabsTrigger>
              </TabsList>

              <TabsContent value={EditRoleTab.GENERAL} className='mt-0 min-h-0 min-w-0 flex-1'>
                <EditRoleGeneralForm
                  ref={generalFormRef}
                  setToast={setToast}
                  role={role}
                  permissions={permissions}
                  onSubmit={onSubmitRole}
                />
              </TabsContent>

              <TabsContent
                value={EditRoleTab.PERMISSIONS}
                className='mt-0 min-h-0 min-w-0 flex-1 overflow-hidden'
              >
                <EditRolePermissionForm
                  ref={permissionFormRef}
                  role={role}
                  tradingAccountLabels={tradingAccountLabels || []}
                  partnershipsAccountLabels={partnershipsAccountLabels || []}
                  clientLabels={clientLabels || []}
                  onSubmit={onSubmitPermissions}
                />
              </TabsContent>
            </Tabs>
          </Loading>
        </div>

        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isLoading || isSubmitting} onClick={handleSave} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
